import * as React from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { getIndividualVenue } from "../helpers/onCallFunctions.ts";
import { ParentVenue, ChildVenue } from "../types/dataTypes.ts";

import { StandardPageHead } from "../components/Heads/StandardPageHead.tsx";
import { NotFoundPage } from "./NotFoundPage.tsx";

import { useFilmListingsController } from "../hooks/useFilmListingsController.ts";
import { FilmListingsSection } from "../components/FilmListingsSection/FilmListingsSection.tsx";
import { LoadingElementInBox } from "../components/Loading/LoadingElement.tsx";

export const FilmListingsPage = () => {
  const [queryStringParameters] = useSearchParams();
  const location = useLocation();

  const cinemasFromQuery = React.useMemo(
    () => queryStringParameters.getAll("cinemas"),
    [queryStringParameters],
  );

  const [firstVenue, setFirstVenue] = React.useState<ParentVenue | null>(null);
  const [venuesNearLocation, setVenuesNearLocation] = React.useState<
    ChildVenue[]
  >([]);
  const [venueNotFound, setVenueNotFound] = React.useState(false);

  const validVenueSlugs = React.useMemo(
    () => venuesNearLocation.map((v) => v.venue_slug),
    [venuesNearLocation],
  );

  // ✅ Always called
  const controller = useFilmListingsController({
    contextSlug: "films-showing",
    initialSelectedVenues: cinemasFromQuery,
    validVenueSlugs,
    defaultOptimise: true,
    enabled: Boolean(firstVenue) && cinemasFromQuery.length > 0,
  });

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  React.useEffect(() => {
    async function loadVenue() {
      try {
        if (cinemasFromQuery.length === 0) {
          setVenueNotFound(true);
          return;
        }

        const firstVenueSlug = cinemasFromQuery[0];
        const venue = await getIndividualVenue(firstVenueSlug);

        if (!venue) {
          setVenueNotFound(true);
          return;
        }

        setFirstVenue(venue);
        setVenuesNearLocation(venue.nearby_venues);
      } catch (e) {
        setVenueNotFound(true);
      }
    }

    loadVenue();
  }, [cinemasFromQuery]);

  if (venueNotFound) {
    return (
      <NotFoundPage
        title="Page not found | CinemaGuide"
        description="This page doesn’t exist (or may have been removed)."
      />
    );
  }

  if (controller.isInitialLoading || !firstVenue) {
    return <LoadingElementInBox />;
  }

  return (
    <>
      <StandardPageHead
        title={`Films showing near ${firstVenue.venue_name} | CinemaGuide`}
        description={`Browse showtimes for ${firstVenue.venue_name} and nearby cinemas. Filter venues and sort films by popularity.`}
        pageUrl={`https://cinemaguideireland.com${location.pathname}${location.search}`}
      />

      <FilmListingsSection
        controller={controller}
        venuesNearLocation={venuesNearLocation}
        selectAllVenuesLabel={`SELECT ALL VENUES NEAR ${firstVenue.venue_name.toUpperCase()}`}
        containerMt={{ xs: 5, md: 5 }}
        containerMb={{ xs: 3, md: 5 }}
      />
    </>
  );
};
